'use client';

import { createContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from './useAuth';
import { permissionsQueryOptions } from './auth-queries';
import type { PermissionContextValue } from './types';

const DEFAULT_VALUE: PermissionContextValue = {
  permissions: [],
  hasPermission: () => false,
  hasAnyPermission: () => false,
  isLoading: true,
  isFetching: false,
};

export const PermissionContext =
  createContext<PermissionContextValue>(DEFAULT_VALUE);

interface PermissionProviderProps {
  children: ReactNode;
}

export function PermissionProvider({ children }: PermissionProviderProps) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const {
    data,
    isLoading: isPermissionsLoading,
    isFetching: isPermissionsFetching,
    isPaused: isPermissionsPaused,
  } = useQuery(permissionsQueryOptions(isAuthenticated));

  const value = useMemo<PermissionContextValue>(() => {
    const permissions = data?.permissions ?? [];
    const permissionSet = new Set(permissions);
    // Treat as loading while auth hasn't resolved (query is disabled)
    // OR while the permissions query is actively fetching
    const isLoading = isAuthLoading || isPermissionsLoading;

    return {
      permissions,
      hasPermission: (permission: string) => permissionSet.has(permission),
      hasAnyPermission: (perms: string[]) =>
        perms.some((p) => permissionSet.has(p)),
      isLoading,
      // React-query's `isLoading` is false while stale permissions are
      // re-read and while a read is paused offline; this says the answer may
      // still change (see `PermissionContextValue.isFetching`).
      isFetching: isPermissionsFetching || isPermissionsPaused,
    };
  }, [
    data?.permissions,
    isAuthLoading,
    isPermissionsLoading,
    isPermissionsFetching,
    isPermissionsPaused,
  ]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}
